import { useCallback, useRef } from 'react';

import { flip, limitShift, shift } from '@floating-ui/react';

import { BlockNoteEditor } from '@/components/atoms/BlockNoteEditor/BlockNoteEditor';
import { Option } from '@/shared/types/rules';
import '@blocknote/shadcn/style.css';
import './ToolkitEditor.scss';

type EditorSize = 'sm' | 'md' | 'lg';

interface ToolkitEditorProps {
  content: string;
  /** Called on every content change - use for live updates */
  onContentChange?: (content: string) => void;
  /** Called when editor loses focus with current content - use for save-on-blur pattern */
  onBlur?: (content: string) => void;
  /** Context variables for autocomplete. Defaults to empty array. */
  variableOptions?: Option[];
  placeholder?: string;
  /**
   * When true, the placeholder shows only while the field is completely empty,
   * not on each focused empty line. Use for single-value fields.
   */
  placeholderOnlyWhenEmpty?: boolean;
  className?: string;
  maxLength?: number;
  /** Editor size: sm (single line like input), md (3-6 lines), lg (6+ lines). Defaults to lg. */
  size?: EditorSize;
  /** Accessibility: indicates invalid state for form validation */
  'aria-invalid'?: boolean | 'true' | 'false';
}

/**
 * Toolkit-specific BlockNote editor wrapper
 * Configured for limited formatting (bold, italic, code, link) and basic lists
 *
 * Supports two update patterns:
 * - Live updates: Use `onContentChange` for real-time sync
 * - Save on blur: Use `onBlur` for efficient updates only when user finishes editing
 */
export function ToolkitEditor({
  content,
  onContentChange,
  onBlur,
  variableOptions = [],
  placeholder = '',
  placeholderOnlyWhenEmpty = false,
  className = '',
  maxLength,
  size = 'lg',
  'aria-invalid': ariaInvalid,
}: ToolkitEditorProps) {
  const contentRef = useRef(content);

  const handleContentChange = useCallback(
    (newContent: string) => {
      contentRef.current = newContent;
      onContentChange?.(newContent);
    },
    [onContentChange]
  );

  const handleBlur = useCallback(() => {
    onBlur?.(contentRef.current);
  }, [onBlur]);

  const isSmallVariant = size === 'sm';

  return (
    <BlockNoteEditor
      content={content}
      onContentChange={handleContentChange}
      variableOptions={variableOptions}
      placeholder={placeholder}
      placeholderOnlyWhenEmpty={placeholderOnlyWhenEmpty}
      formattingButtons={isSmallVariant ? [] : ['bold', 'italic', 'code', 'link']}
      allowedSlashKeys={isSmallVariant ? [] : ['numbered_list', 'bullet_list']}
      maxLength={maxLength}
      enforceMaxLength={true}
      className={`toolkit-editor toolkit-editor--${size} ${className}`}
      toolbarFloatingOptions={{ strategy: 'absolute', middleware: [flip({ padding: 8 })] }}
      linkToolbarFloatingOptions={{
        strategy: 'absolute',
        middleware: [flip({ padding: 8 }), shift({ padding: 8, limiter: limitShift() })],
      }}
      onBlur={handleBlur}
      aria-invalid={ariaInvalid}
    />
  );
}
